"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  trackAiNexusClick,
  trackRouteChange,
  trackScheduleClick,
} from "@/lib/analytics";

// Metadata is applied after the route commits, so reading document.title in the
// effect would report the previous page. A short delay lets it settle.
const TITLE_SETTLE_MS = 100;

// Matched on hostname rather than the href string: the three links to this site
// are written inconsistently (with and without a trailing slash).
const AI_NEXUS_HOST = "ai-nexus.icieos.com";

/**
 * Bridges App Router navigation and global CTA clicks onto the dataLayer.
 *
 * GTM's built-in Page View trigger only fires when the container initialises,
 * which on a client-routed app means the first hard load and nothing after it.
 * Without this, every soft navigation is invisible to GA4 and the Meta Pixel.
 */
export default function AnalyticsTracker() {
  const pathname = usePathname();
  // The container's own Page View covers the first render; pushing here too
  // would double-count the landing page.
  const hasTrackedInitialView = useRef(false);

  useEffect(() => {
    // Studio is the internal Sanity CMS, not marketing surface.
    if (!pathname || pathname.startsWith("/studio")) return;

    if (!hasTrackedInitialView.current) {
      hasTrackedInitialView.current = true;
      return;
    }

    const timer = setTimeout(() => {
      trackRouteChange({ pagePath: pathname, pageTitle: document.title });
    }, TITLE_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    // Both consultation CTAs and AI Nexus links are rendered by several
    // different components, so match on the destination instead of wiring an
    // onClick into each one. Capture phase, so a child calling
    // stopPropagation cannot swallow the event.
    const handleTrackedClick = (event) => {
      const link = event.target?.closest?.("a[href]");
      if (!link) return;

      const linkText = link.textContent?.trim().slice(0, 80);

      if (link.getAttribute("href")?.startsWith("/schedule")) {
        trackScheduleClick({ linkText });
        return;
      }

      if (link.hostname === AI_NEXUS_HOST) {
        trackAiNexusClick({ linkText, sourcePage: window.location.pathname });
      }
    };

    document.addEventListener("click", handleTrackedClick, true);
    return () =>
      document.removeEventListener("click", handleTrackedClick, true);
  }, []);

  return null;
}
